import { useState } from 'react';
import { useQuery } from 'react-query';

import { getBornLabel } from '@aldebaran/common/util/patientDataCollection';

import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/useDebounce';
import {
  GEOCODING_MIN_QUERY_LENGTH,
  searchCommunes,
  type CommuneSuggestion,
} from '@/util/geocoding';

import { AutocompleteInput } from './AutocompleteInput';
import { FieldHelper, FieldLabel } from './FieldHelper';

export type BirthPlaceValue = {
  birthPlace: string | null;
  birthPlaceInseeCode: string | null;
};

type CommuneAutocompleteFieldProps = {
  value: BirthPlaceValue;
  onChange: (value: BirthPlaceValue) => void;
  label?: string;
  // Parent-driven error, e.g. "required" on submit. Takes precedence.
  error?: string;
  optional?: boolean;
  // False when someone fills the form for the patient (interlocuter OTHER).
  isSelf?: boolean;
  sex?: string | null;
  // Reports the field's own error (typed text not picked from the list), which
  // the parent can't see since that text never reaches `value`.
  onLocalErrorChange?: (error: string | undefined) => void;
};

export const CommuneAutocompleteField = ({
  value,
  onChange,
  label = 'Commune de naissance',
  error,
  optional,
  isSelf = true,
  sex,
  onLocalErrorChange,
}: CommuneAutocompleteFieldProps) => {
  // Free-text mode: born outside France (no INSEE commune), or the search
  // service is unavailable. Starts there for a known place without a code.
  const [freeText, setFreeText] = useState(
    !!value.birthPlace && !value.birthPlaceInseeCode,
  );
  const [query, setQuery] = useState(value.birthPlace ?? '');
  const [selectionError, setSelectionErrorState] = useState<string>();
  const setSelectionError = (next: string | undefined) => {
    setSelectionErrorState(next);
    onLocalErrorChange?.(next);
  };
  const debouncedQuery = useDebounce(query.trim(), 300);

  const isSearchable =
    !freeText &&
    !value.birthPlaceInseeCode &&
    debouncedQuery.length >= GEOCODING_MIN_QUERY_LENGTH;

  const {
    data: suggestions = [],
    isFetching,
    isError,
    isSuccess,
  } = useQuery(
    ['geocoding', 'commune', debouncedQuery],
    ({ signal }) => searchCommunes(debouncedQuery, signal),
    { enabled: isSearchable, staleTime: Infinity, retry: 1 },
  );

  const displayedError = error ?? selectionError;

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel optional={optional}>{label}</FieldLabel>

      {freeText ? (
        <Input
          aria-label={label}
          value={value.birthPlace ?? ''}
          placeholder="Ville et pays de naissance"
          aria-invalid={!!displayedError || undefined}
          onChange={(event) =>
            onChange({
              birthPlace: event.target.value || null,
              birthPlaceInseeCode: null,
            })
          }
        />
      ) : (
        <AutocompleteInput<CommuneSuggestion>
          ariaLabel={label}
          value={query}
          placeholder="Toulouse"
          onValueChange={(next) => {
            setQuery(next);
            setSelectionError(undefined);
            // Typing again invalidates the previously selected commune.
            if (value.birthPlace || value.birthPlaceInseeCode)
              onChange({ birthPlace: null, birthPlaceInseeCode: null });
          }}
          suggestions={isSearchable ? suggestions : []}
          getSuggestionKey={(commune) => commune.inseeCode}
          renderSuggestion={(commune) => (
            <>
              <span className="font-medium">{commune.name}</span>
              <span className="text-gray-500"> ({commune.context})</span>
            </>
          )}
          onSelect={(commune) => {
            setQuery(commune.name);
            setSelectionError(undefined);
            onChange({
              birthPlace: commune.name,
              birthPlaceInseeCode: commune.inseeCode,
            });
          }}
          isLoading={isFetching}
          showEmptyMessage={isSearchable && isSuccess && !isFetching}
          emptyMessage="Aucune commune trouvée"
          invalid={!!displayedError}
          onBlur={() =>
            setSelectionError(
              query.trim() && !value.birthPlaceInseeCode
                ? isSelf
                  ? 'Sélectionnez votre commune dans la liste'
                  : 'Sélectionnez la commune dans la liste'
                : undefined,
            )
          }
        />
      )}

      <FieldHelper error={!!displayedError || (isError && !freeText)}>
        {displayedError ??
          (isError && !freeText
            ? 'La recherche de communes est indisponible, saisissez-la manuellement.'
            : undefined)}
      </FieldHelper>

      <button
        type="button"
        className="w-fit cursor-pointer text-xs font-medium text-indigo-900 underline"
        onClick={() => {
          setFreeText(!freeText);
          setQuery('');
          setSelectionError(undefined);
          onChange({ birthPlace: null, birthPlaceInseeCode: null });
        }}
      >
        {freeText
          ? 'Rechercher une commune française'
          : isError
          ? 'Saisir manuellement'
          : `${getBornLabel(sex)} hors de France ?`}
      </button>
    </div>
  );
};
